import React, { useEffect, useRef, useState, useMemo, memo, useCallback } from 'react';
const MessageBubble = React.lazy(() => import('./MessageBubble').then(module => ({ default: module.MessageBubble })));
import type { ClarifySubmitPayload } from './ClarifyOptionsPanel';
import { ChatInput } from './ChatInput';
import { ChatSession, Message, GenerationConfig, GenerationTestHints, ScenarioTemplate, languageForBrand } from '../types';
import { ScanEye, Cpu, ShieldAlert, Activity, Filter, Bell, Database, Package, Thermometer, ChevronDown, RotateCcw, Loader2 } from 'lucide-react';
import { SAMPLE_PROMPTS } from '../constants';
import { getEffectiveLanguage, useTranslation } from '../i18n';
import { appLogoUrl } from '../assets';
import { getSavedPlcLanguage, getSavedPlcTargetProfile } from '../utils/plcPreferences';
import { sessionSettingsPlcModel, type TurnGenerationConfig } from '../utils/sessionTiaTarget';
import {
  buildProviderCredentialMaps,
  getSelectedProviderRuntimeConfig,
  normalizeProvider,
  resolveModelSelection,
} from '../utils/aiModelSettings';
import { getItem } from '../utils/scopedSettingsStorage';
import { messageHasCompletedProjectSummary } from '../utils/planReviewState';
import { getWorkflowStageGates } from '../utils/workflowStageGuards';
import { rebuildClarifyFollowUp } from '../utils/clarifyRetryState';
import { ensureAttentionPermission, useAttentionRecall } from '../hooks/useAttentionRecall';
import type { WorkspaceTab } from '../utils/workspaceViewModel';
import { getVariantInfo, type ActiveBranchMap } from '../utils/conversationPath';
import { findPreviousGeneration } from '../utils/generationLineage';
import { splitClarifySubmitConfig } from '../utils/clarifySubmitConfig';
import { markResumedPlanMessages } from '../utils/resumePlanIntent';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { ConnectionBanner } from './ConnectionBanner';

/** Breathing room at both ends of the virtualized list. */
const VIRTUOSO_SPACERS = {
  Header: () => <div className="h-6" />,
  Footer: () => <div className="h-6" />,
};

type ExpertCard = {
  id: string;
  title: string;
  prompt: string;
  badgeLabel: string;
  IconComponent: React.ComponentType<{ size?: number; className?: string }>;
  bgGradient: string;
  iconColor: string;
  scenarioTemplateId: string;
  translate?: boolean;
  _rawBadgeLabel?: string;
};

const FALLBACK_EXPERT_CARD_META = [
  { id: 'expert-conveyor-control', bgGradient: 'from-gray-50 to-gray-100 dark:from-emerald-500/10 dark:to-teal-500/5', IconComponent: Cpu, iconColor: 'text-gray-600 dark:text-emerald-400', titleKey: 'Conveyor Control', badgeLabel: 'Motion' },
  { id: 'expert-pid-loop', bgGradient: 'from-gray-50 to-gray-100 dark:from-blue-500/10 dark:to-cyan-500/5', IconComponent: Activity, iconColor: 'text-gray-600 dark:text-blue-400', titleKey: 'PID Loop', badgeLabel: 'PID' },
  { id: 'expert-traffic-sequence', bgGradient: 'from-gray-50 to-gray-100 dark:from-purple-500/10 dark:to-pink-500/5', IconComponent: ScanEye, iconColor: 'text-gray-600 dark:text-purple-400', titleKey: 'Traffic Sequence', badgeLabel: 'Sequencing' },
  { id: 'expert-motor-starter', bgGradient: 'from-gray-50 to-gray-100 dark:from-amber-500/10 dark:to-orange-500/5', IconComponent: ShieldAlert, iconColor: 'text-gray-600 dark:text-amber-400', titleKey: 'Motor Starter', badgeLabel: 'Motor' },
  { id: 'expert-tank-control', bgGradient: 'from-gray-50 to-gray-100 dark:from-indigo-500/10 dark:to-blue-500/5', IconComponent: Filter, iconColor: 'text-gray-600 dark:text-indigo-400', titleKey: 'Tank Control', badgeLabel: 'Process' },
  { id: 'expert-batch-process', bgGradient: 'from-gray-50 to-gray-100 dark:from-rose-500/10 dark:to-red-500/5', IconComponent: Cpu, iconColor: 'text-gray-600 dark:text-rose-400', titleKey: 'Batch Process', badgeLabel: 'Process' },
  { id: 'expert-alarm-manager', bgGradient: 'from-gray-50 to-gray-100 dark:from-red-500/10 dark:to-rose-500/5', IconComponent: Bell, iconColor: 'text-gray-600 dark:text-red-400', titleKey: 'Alarm Manager', badgeLabel: 'Safety' },
  { id: 'expert-data-logger', bgGradient: 'from-gray-50 to-gray-100 dark:from-lime-500/10 dark:to-green-500/5', IconComponent: Database, iconColor: 'text-gray-600 dark:text-lime-400', titleKey: 'Data Logger', badgeLabel: 'Data' },
  { id: 'expert-packaging-line', bgGradient: 'from-gray-50 to-gray-100 dark:from-fuchsia-500/10 dark:to-pink-500/5', IconComponent: Package, iconColor: 'text-gray-600 dark:text-fuchsia-400', titleKey: 'Packaging Line', badgeLabel: 'Sequencing' },
  { id: 'expert-hvac-control', bgGradient: 'from-gray-50 to-gray-100 dark:from-sky-500/10 dark:to-blue-500/5', IconComponent: Thermometer, iconColor: 'text-gray-600 dark:text-sky-400', titleKey: 'HVAC Control', badgeLabel: 'HVAC' },
];

const FALLBACK_EXPERT_CARD_IDS = new Set(FALLBACK_EXPERT_CARD_META.map((meta) => meta.id));

const TEMPLATE_ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  conveyor: Cpu,
  pid: Activity,
  sequence: ScanEye,
  motor: ShieldAlert,
  tank: Filter,
  batch: Cpu,
  alarm: Bell,
  data: Database,
  packaging: Package,
  hvac: Thermometer,
};

const TEMPLATE_STYLE_MAP: Record<string, { bgGradient: string; iconColor: string }> = {
  conveyor: { bgGradient: 'from-gray-50 to-gray-100 dark:from-emerald-500/10 dark:to-teal-500/5', iconColor: 'text-gray-600 dark:text-emerald-400' },
  pid: { bgGradient: 'from-gray-50 to-gray-100 dark:from-blue-500/10 dark:to-cyan-500/5', iconColor: 'text-gray-600 dark:text-blue-400' },
  sequence: { bgGradient: 'from-gray-50 to-gray-100 dark:from-purple-500/10 dark:to-pink-500/5', iconColor: 'text-gray-600 dark:text-purple-400' },
  motor: { bgGradient: 'from-gray-50 to-gray-100 dark:from-amber-500/10 dark:to-orange-500/5', iconColor: 'text-gray-600 dark:text-amber-400' },
  tank: { bgGradient: 'from-gray-50 to-gray-100 dark:from-indigo-500/10 dark:to-blue-500/5', iconColor: 'text-gray-600 dark:text-indigo-400' },
  batch: { bgGradient: 'from-gray-50 to-gray-100 dark:from-rose-500/10 dark:to-red-500/5', iconColor: 'text-gray-600 dark:text-rose-400' },
  alarm: { bgGradient: 'from-gray-50 to-gray-100 dark:from-red-500/10 dark:to-rose-500/5', iconColor: 'text-gray-600 dark:text-red-400' },
  data: { bgGradient: 'from-gray-50 to-gray-100 dark:from-lime-500/10 dark:to-green-500/5', iconColor: 'text-gray-600 dark:text-lime-400' },
  packaging: { bgGradient: 'from-gray-50 to-gray-100 dark:from-fuchsia-500/10 dark:to-pink-500/5', iconColor: 'text-gray-600 dark:text-fuchsia-400' },
  hvac: { bgGradient: 'from-gray-50 to-gray-100 dark:from-sky-500/10 dark:to-blue-500/5', iconColor: 'text-gray-600 dark:text-sky-400' },
};

type ScenarioTemplatePayload = ScenarioTemplate & {
  name?: string;
  description?: string;
  vendor?: string;
};

function firstText(...values: Array<unknown>): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function hasDisplayContent(template: ScenarioTemplatePayload): boolean {
  return Boolean(
    firstText(template.title, template.name) &&
    firstText(template.starterPrompt, template.summary, template.description)
  );
}

function inferTemplateIconKey(template: ScenarioTemplatePayload): string {
  const value = `${template.id} ${template.taskType || ''} ${template.title || template.name || ''}`.toLowerCase();
  if (value.includes('pid') || value.includes('temperature')) return 'pid';
  if (value.includes('traffic') || value.includes('sequence')) return 'sequence';
  if (value.includes('motor')) return 'motor';
  if (value.includes('tank')) return 'tank';
  if (value.includes('batch')) return 'batch';
  if (value.includes('alarm')) return 'alarm';
  if (value.includes('data') || value.includes('logger')) return 'data';
  if (value.includes('packaging')) return 'packaging';
  if (value.includes('hvac')) return 'hvac';
  if (value.includes('conveyor')) return 'conveyor';
  return 'sequence';
}

function inferTemplateBadge(template: ScenarioTemplatePayload): string {
  const value = `${template.id} ${template.taskType || ''} ${template.title || template.name || ''}`.toLowerCase();
  if (value.includes('pid') || value.includes('temperature')) return 'PID';
  if (value.includes('motor')) return 'Motor';
  if (value.includes('tank') || value.includes('batch')) return 'Process';
  if (value.includes('alarm')) return 'Safety';
  if (value.includes('data') || value.includes('logger')) return 'Data';
  if (value.includes('hvac')) return 'HVAC';
  if (value.includes('traffic') || value.includes('packaging') || value.includes('sequence')) return 'Sequencing';
  if (value.includes('conveyor')) return 'Motion';
  return 'Template';
}

/**
 * Generation settings read straight from saved preferences.
 *
 * Used when a turn needs to be sent but no config was captured this session —
 * a reloaded history, or a follow-up chip clicked before the user has typed.
 */
export function buildDefaultGenerationConfig(projectId?: string): TurnGenerationConfig {
  const savedProvider = normalizeProvider(getItem('ai_provider'));
  const savedModel = getItem('ai_model') || getItem('thinking_ai_model') || 'autofree';
  const resolved = resolveModelSelection(savedProvider, savedModel);
  const { apiKey, baseUrl } = getSelectedProviderRuntimeConfig(resolved.provider);
  const { providerApiKeys, providerBaseUrls } = buildProviderCredentialMaps();
  const targetProfile = getSavedPlcTargetProfile();

  return {
    brand: targetProfile.brand,
    language: languageForBrand(targetProfile.brand, getSavedPlcLanguage()),
    context: '',
    projectId,
    includeComments: true,
    addSafetyRoutines: false,
    strictSafetyCheck: getItem('strict_safety_check') === 'true',
    customInstructions: getItem('custom_instructions') || '',
    model: resolved.model,
    preferredModel: resolved.model,
    preferredProvider: resolved.provider,
    useWebSearch: false,
    apiKey,
    baseUrl,
    providerApiKeys,
    providerBaseUrls,
    uiLanguage: getEffectiveLanguage(),
    routingMode: (getItem('routing_mode') as 'fast' | 'thinking') || 'fast',
    knowledgeScope: getItem('knowledge_scope') || undefined,
    targetProfile,
    softwareVersion: targetProfile.softwareVersion,
    hardwareSeries: targetProfile.hardwareSeries,
    settingsPlcModel: targetProfile.plcModel,
    firmwareVersion: targetProfile.firmwareVersion,
  };
}

function buildFallbackExpertCards(prompts: string[]): ExpertCard[] {
  return prompts.slice(0, FALLBACK_EXPERT_CARD_META.length).map((prompt, index) => {
    const meta = FALLBACK_EXPERT_CARD_META[index];
    return {
      id: meta.id,
      title: meta.titleKey,
      prompt,
      badgeLabel: meta.badgeLabel,
      IconComponent: meta.IconComponent,
      bgGradient: meta.bgGradient,
      iconColor: meta.iconColor,
      scenarioTemplateId: meta.id,
      translate: true,
    };
  });
}

function buildCustomTemplateCard(template: ScenarioTemplate): ExpertCard | null {
  const payload = template as ScenarioTemplatePayload;
  const title = firstText(payload.title, payload.name, payload.id);
  const prompt = firstText(payload.starterPrompt, payload.summary, payload.description, title);
  if (!title || !prompt) return null;

  const iconKey = payload.iconKey || inferTemplateIconKey(payload);
  const style = TEMPLATE_STYLE_MAP[iconKey] || TEMPLATE_STYLE_MAP.sequence;
  const badgeLabel = firstText(payload.badgeLabel, payload.category === 'general' ? '' : payload.category, inferTemplateBadge(payload));
  return {
    id: payload.id,
    title,
    prompt,
    badgeLabel: badgeLabel,
    IconComponent: TEMPLATE_ICON_MAP[iconKey] || ScanEye,
    bgGradient: style.bgGradient,
    iconColor: style.iconColor,
    scenarioTemplateId: payload.id,
    translate: true,
    _rawBadgeLabel: badgeLabel,
  };
}

function buildTemplateExpertCards(templates: ScenarioTemplate[]): ExpertCard[] {
  const templatesById = new Map(templates.map((template) => [template.id, template]));
  const fallbackCards = buildFallbackExpertCards(SAMPLE_PROMPTS).map((card) => {
    const template = templatesById.get(card.id);
    return template ? { ...card, scenarioTemplateId: template.id } : card;
  });

  const customCards = templates
    .filter((template) => !FALLBACK_EXPERT_CARD_IDS.has(template.id))
    .map(buildCustomTemplateCard)
    .filter((card): card is ExpertCard => Boolean(card));

  return [...fallbackCards, ...customCards];
}

/**
 * 这次发送来自需求确认卡片，不是用户打的字。
 *
 * 注意不能拿 config.isClarifyFollowUp 当这个信号——用户在卡片还开着时手打
 * 「继续」，那条也是 clarify 跟进，但它是真的用户消息，必须照常显示。
 */
export interface ClarifyEchoOptions {
  /** 提问的那条 assistant 消息，答案要盖回它的卡片上。 */
  answeredMessageId: string;
  answers: NonNullable<Message['clarifyAnswers']>;
}

interface ChatAreaProps {
  /** The branch currently on screen. */
  messages: Message[];
  /** Every stored message including abandoned branches; drives the variant switcher. */
  allMessages?: Message[];
  activeBranches?: ActiveBranchMap;
  onSelectBranch?: (groupId: string, branchId: string) => void;
  isLoading: boolean;
  historyState?: 'loading' | 'error' | null;
  onRetryHistory?: () => void;
  onNewConversation?: () => void;
  onSend: (
    prompt: string,
    config: GenerationConfig,
    options?: { replaceFromMessageId?: string; clarifyEcho?: ClarifyEchoOptions },
  ) => void;
  onStop?: () => void;
  activeSession?: ChatSession | null;
  activeProjectId?: string | null;
  isSuperAdmin?: boolean;
  isAuthenticated?: boolean;
  onPlanReviewSubmitted?: (
    messageId: string,
    action: NonNullable<Message['planReview']>['reviewResult'],
    approvalSource?: NonNullable<Message['planReview']>['approvalSource'],
    /** The proposed signals ticked on the card, when the decision carried a list of them. */
    acceptedSignals?: string[],
  ) => void;
  onResumePlan?: (planReview: NonNullable<Message['planReview']>, requestId: string) => void;
  onOpenRequirementWorkbook?: (messageId: string, tab?: WorkspaceTab) => void;
  /** TA9d: writes a resolved test-hints reading into this session's own state, once. */
  onTestHintsResolved?: (sessionId: string, messageId: string, testHints: GenerationTestHints) => void;
}

/**
 * The user turn an assistant turn is answering, so the answer can open by
 * saying what it heard. Read from the conversation rather than from the model,
 * so it is on screen before the first token arrives.
 */
function requestAnsweredBy(
  messages: Message[],
  index: number,
): { text?: string; fileNames?: string[] } | undefined {
  if (messages[index]?.role !== 'assistant') return undefined;
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const candidate = messages[cursor];
    if (candidate.role !== 'user') continue;
    return {
      text: candidate.content,
      fileNames: (candidate.attachments || []).map(file => file.fileName).filter(Boolean),
    };
  }
  return undefined;
}

const ChatAreaComponent: React.FC<ChatAreaProps> = ({ messages, allMessages, activeBranches, onSelectBranch, isLoading, historyState = null, onRetryHistory, onNewConversation, onSend, onStop, activeSession, activeProjectId, isSuperAdmin, isAuthenticated = false, onPlanReviewSubmitted, onResumePlan, onOpenRequirementWorkbook, onTestHintsResolved }) => {
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  // Rows the list can actually measure.
  //
  // MessageBubble returns null for a clarifyEcho message — the app echoing the
  // answers the user picked from cards, which is sent to the model but must not
  // be drawn as something the user typed. It was still an entry in the data
  // array, so Virtuoso measured it, got zero, and logged
  // "Zero-sized element, this should not happen" for each one — 25+ of them in
  // the 2026-09-07 walkthrough. A row that renders nothing does not belong in a
  // virtualised list; the original index is kept so neighbours still resolve.
  // The message 「继续执行」 sent is drawn as one line of what was done, never as the user's words.
  const visibleMessages = useMemo(
    () => markResumedPlanMessages(messages).map((msg, index) => ({ msg, index })).filter(entry => !entry.msg.clarifyEcho),
    [messages],
  );

  const [showAllPrompts, setShowAllPrompts] = useState(false);
  const [expertTemplates, setExpertTemplates] = useState<ScenarioTemplate[]>([]);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [expertDraft, setExpertDraft] = useState<{ id: number; text: string } | null>(null);
  const { t } = useTranslation();
  const lastConfigRef = useRef<GenerationConfig | null>(null);
  const resolvedProjectId = activeSession?.projectId || activeProjectId || undefined;
  const sessionGenerationConfig = useMemo<TurnGenerationConfig | null>(() => activeSession?.sessionConfig
    ? {
        brand: activeSession.sessionConfig.brand,
        language: activeSession.sessionConfig.language,
        context: '',
        projectId: resolvedProjectId,
        includeComments: true,
        addSafetyRoutines: false,
        strictSafetyCheck: true,
        customInstructions: getItem('custom_instructions') || '',
        model: activeSession.sessionConfig.preferredModel || activeSession.lastModel || 'autofree',
        preferredModel: activeSession.sessionConfig.preferredModel || activeSession.lastModel || 'autofree',
        preferredProvider: activeSession.sessionConfig.preferredProvider,
        routingMode: activeSession.sessionConfig.routingMode,
        targetProfile: activeSession.sessionConfig.targetProfile,
        softwareVersion: activeSession.sessionConfig.softwareVersion,
        hardwareSeries: activeSession.sessionConfig.hardwareSeries,
        // The user's setting: the snapshot's `plcModel` is the server's (P2 E9).
        settingsPlcModel: sessionSettingsPlcModel(activeSession.sessionConfig),
        firmwareVersion: activeSession.sessionConfig.firmwareVersion,
      }
    : null, [activeSession, resolvedProjectId]);

  useEffect(() => {
    // Rehydrate retry settings when opening a historical session. Without this,
    // the visible retry action silently did nothing after refresh/session switch.
    lastConfigRef.current = sessionGenerationConfig;
  }, [activeSession?.id, sessionGenerationConfig]);

  const handleSend = useCallback((prompt: string, config: GenerationConfig) => {
    lastConfigRef.current = config;
    // Sending is the user gesture a permission prompt needs, and the moment they
    // commit to a run long enough that they will not sit and watch it.
    ensureAttentionPermission();
    onSend(prompt, config);
  }, [onSend]);

  /**
   * A run that has stopped and is waiting on the user, while the user is very
   * likely elsewhere. Empty when nothing is waiting.
   */
  const attentionLabel = useMemo(() => {
    if (isLoading || messages.length === 0) return '';
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || last.isStreaming) return '';
    if (last.progressState?.phase === 'failed' || last.progressState?.phase === 'stopped') return '';
    const gates = getWorkflowStageGates(last);
    if (gates.hasPendingHumanConfirmation) return t('Waiting for your confirmation');
    if (gates.hasPlanReview && !gates.planApprovalResolvedOrNotRequired) return t('Waiting for your plan approval');
    return '';
  }, [isLoading, messages, t]);

  useAttentionRecall(Boolean(attentionLabel), attentionLabel);

  // Edit an earlier prompt: rewrite the conversation from that turn and
  // regenerate, rather than appending a corrected copy after the wrong one.
  const handleResubmitUserMessage = useCallback((messageId: string, content: string) => {
    const config = lastConfigRef.current || sessionGenerationConfig || buildDefaultGenerationConfig(resolvedProjectId);
    lastConfigRef.current = config;
    onSend(content, config, { replaceFromMessageId: messageId });
  }, [onSend, resolvedProjectId, sessionGenerationConfig]);

  // P0-1: Retry last message
  const handleRetryLastMessage = useCallback(() => {
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
    const retryConfig = lastConfigRef.current || sessionGenerationConfig;
    if (!lastUserMsg || !retryConfig) return;

    const config: GenerationConfig = { ...retryConfig };

    // A retry of a confirmation turn is still that turn.
    //
    // `lastConfigRef` holds the config the send used, and it is a ref: a
    // reload, a remount or a session switch empties it, and the retry then
    // falls back to the session's plain config — no `isClarifyFollowUp`, no
    // `clarifyState`. The body of a confirmation turn is only 「问题: 答案」, so
    // the backend, told this is not a follow-up, takes that as the entire
    // requirement. On 2026-09-09 a conveyor came back as 「PLC 控制执行器驱动、
    // 超时停机、报警锁存及安全联锁的需求」 — the card topics read as the machine,
    // the conveyor gone — and a fresh round of cards was asked over the top of
    // thirteen just answered. The pinned model rides in the same state, which
    // is why that turn also switched itself to AutoFree.
    //
    // The state is in the messages, so it is rebuilt from there rather than
    // trusted to survive in memory — the asking turn carries both the state as
    // it stood and the answers given to it, and merging them is what the submit
    // path does too.
    if (lastUserMsg.clarifyEcho && !config.clarifyState) {
      Object.assign(config, rebuildClarifyFollowUp(messages) || {});
    }

    onSend(lastUserMsg.content, config);
  }, [messages, onSend, sessionGenerationConfig]);

  /**
   * Continue a run the user stopped.
   *
   * This is a fresh turn that carries the partial output forward, not a resumed
   * stream — the backend has no checkpoint to restart from, and pretending
   * otherwise would misrepresent what happened.
   */
  const stoppedMessage = useMemo(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || last.isStreaming) return null;
    return last.progressState?.phase === 'stopped' ? last : null;
  }, [messages]);

  const handleContinueGeneration = useCallback(() => {
    if (!stoppedMessage) return;
    const config = lastConfigRef.current || sessionGenerationConfig || buildDefaultGenerationConfig(resolvedProjectId);
    lastConfigRef.current = config;
    onSend(t('Continue from where the previous answer stopped.'), config);
  }, [onSend, resolvedProjectId, sessionGenerationConfig, stoppedMessage, t]);

  // Follow-up suggestion — send as a new user message.
  // A reloaded session may have neither a remembered config nor a session config;
  // falling back to the composer's defaults keeps the chip from silently doing
  // nothing, which is worse than sending with default settings.
  const handleFollowUpSend = useCallback((
    text: string,
    options?: { validationExplanation?: boolean },
  ) => {
    const base = lastConfigRef.current || sessionGenerationConfig || buildDefaultGenerationConfig(resolvedProjectId);
    // Remembered without the flag on purpose: the explanation is one turn, and
    // leaving `isValidationExplanation` in the sticky config would pin every
    // later prompt in this session to answer-only and quietly stop the app
    // generating programs.
    lastConfigRef.current = base;
    onSend(text, options?.validationExplanation
      ? { ...base, isValidationExplanation: true }
      : base);
  }, [onSend, resolvedProjectId, sessionGenerationConfig]);

  const hasActiveClarify = useMemo(() => {
    if (messages.length === 0) return false;
    const last = messages[messages.length - 1];
    // Don't block input if the last generation was stopped or failed
    if (last.progressState?.phase === 'failed' || last.progressState?.phase === 'stopped') return false;
    return last.role === 'assistant'
      && !last.isStreaming
      && !messageHasCompletedProjectSummary(last)
      && ((last.clarificationChecklist?.length ?? 0) > 0);
  }, [messages]);

  // Extract active clarifyState so follow-up text input (e.g. "继续" after
  // token quota interruption) carries the clarify context forward.
  const activeClarifyState = useMemo(() => {
    if (!hasActiveClarify) return undefined;
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    return (lastAssistant as any)?.clarifyState as GenerationConfig['clarifyState'] | undefined;
  }, [hasActiveClarify, messages]);

  useEffect(() => {
    let isMounted = true;

    // The featured-template endpoint is authenticated. During logout / token restore
    // ChatArea can briefly mount before auth is ready, so do not call it until the
    // parent confirms the user is authenticated. This prevents noisy 401 requests
    // and avoids replacing the fallback cards with an error state.
    if (!isAuthenticated) {
      setExpertTemplates([]);
      return () => {
        isMounted = false;
      };
    }

    const savedKnowledgeScope = getItem('knowledge_scope') || '';
    const scopeQuery = savedKnowledgeScope ? `&scope=${encodeURIComponent(savedKnowledgeScope)}` : '';

    fetch(`/api/templates?featured=true${scopeQuery}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Template request failed: ${response.status}`);
        }
        return response.json();
      })
      .then((payload) => {
        if (!isMounted) return;
        const templates = Array.isArray(payload?.templates)
          ? payload.templates.filter(hasDisplayContent)
          : [];
        setExpertTemplates(templates);
      })
      .catch(() => {
        if (!isMounted) return;
        setExpertTemplates([]);
      });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  const expertCards = useMemo(() => {
    if (expertTemplates.length > 0) {
      return buildTemplateExpertCards(expertTemplates);
    }
    return buildFallbackExpertCards(SAMPLE_PROMPTS);
  }, [expertTemplates]);

  // Collapsed the suggestion wall to a short chip row: on an empty screen the
  // input box should be what the eye lands on, not ten gradient cards.
  const visibleExpertCards = useMemo(() =>
    showAllPrompts ? expertCards : expertCards.slice(0, 4),
    [expertCards, showAllPrompts]
  );

  const selectExpertCard = useCallback((card: ExpertCard) => {
    // Suggestions are drafts, not send buttons. Let the user inspect and edit
    // the requirement before committing a potentially long generation run.
    setExpertDraft({
      id: Date.now(),
      text: card.translate ? t(card.prompt) : card.prompt,
    });
  }, [t]);

  // Scroll following is Virtuoso's job now (see `followOutput`); this only has
  // to take the user back to the end when they ask for it.
  const jumpToLatest = useCallback(() => {
    virtuosoRef.current?.scrollToIndex({ index: 'LAST', align: 'end', behavior: 'smooth' });
    setShowJumpToLatest(false);
  }, []);

  /**
   * One turn. Extracted so the list can be virtualized: only the rows near the
   * viewport mount, which matters once a session holds several 200-line programs.
   */
  const renderMessage = useCallback((msg: Message, messageIndex: number) => (
                  <React.Suspense
      key={msg.id}
      fallback={<div aria-hidden="true" className="mx-auto h-12 w-full max-w-[46rem] animate-pulse px-4 py-3" />}
    >
    <MessageBubble
      message={msg}
      projectId={resolvedProjectId}
      sessionId={activeSession?.id}
      sessionModelLabel={activeSession?.sessionConfig?.preferredModel}
      onPlanReviewSubmitted={onPlanReviewSubmitted}
      onResumePlan={onResumePlan}
      onOpenRequirementWorkbook={onOpenRequirementWorkbook}
      onRetryLastMessage={handleRetryLastMessage}
      isLastMessage={messageIndex === messages.length - 1}
      userRequest={requestAnsweredBy(messages, messageIndex)}
      previousGeneration={findPreviousGeneration(messages, messageIndex)}
      variantInfo={getVariantInfo(msg, allMessages || messages, activeBranches)}
      onSelectVariant={onSelectBranch}
      onResubmitUserMessage={handleResubmitUserMessage}
      onFollowUpSend={handleFollowUpSend}
      onTestHintsResolved={onTestHintsResolved}
      onClarifySubmit={(payload: ClarifySubmitPayload) => {
        const baseFollowUpConfig = lastConfigRef.current || sessionGenerationConfig;
        const followUpConfig: GenerationConfig | null = baseFollowUpConfig
          ? { ...baseFollowUpConfig }
          : null;
        if (followUpConfig) {
          followUpConfig.isClarifyFollowUp = true;
          // Clear stale attachment IDs — clarify follow-ups don't re-send files
          delete (followUpConfig as any).attachmentIds;
          delete (followUpConfig as any).attachments;
          // Merge new answers with existing clarifyState.
          // Try msg.clarifyState first; if missing (e.g. after page
          // refresh or stale closure), walk messages backwards to find
          // the last assistant message that has clarifyState.
          let prevState = (msg as any).clarifyState;
          if (!prevState) {
            // Fallback: scan messages from end to find the last
            // assistant message with clarifyState.
            const fallbackMsg = [...messages].reverse().find(
              m => m.role === 'assistant' && (m as any).clarifyState,
            );
            if (fallbackMsg) {
              prevState = (fallbackMsg as any).clarifyState;
            }
          }
          const newAnswers = payload.answers.map(a => ({
            // The card id is what the panel restores a selection by; dropping it
            // here made a persisted answer come back as an empty card.
            id: a.id,
            category: a.category,
            question: a.question,
            selectedOptions: a.selectedOptions,
            fieldKeys: a.fieldKeys,
          }));
          followUpConfig.clarifyState = {
            clarifyRound: prevState?.clarifyRound ?? 0,
            pinnedProvider: prevState?.pinnedProvider,
            pinnedModel: prevState?.pinnedModel,
            confirmedAnswers: [
              ...(prevState?.confirmedAnswers ?? []),
              ...newAnswers,
            ],
            askedQuestionKeys: prevState?.askedQuestionKeys ?? [],
          };
          // 这一次提交要说出它回答的是哪张单子，服务端据此认领、拒绝第二次确认；
          // 留给重试/继续/批准复用的那份配置不带这个名字，见 splitClarifySubmitConfig。
          // 报的是提问那轮的 requestId——服务端认得的那个名字；msg.id 是浏览器自己起的，
          // 服务端那一行叫 `${requestId}:assistant`，两边从来对不上（29da7eb）。
          const { reusable, request } = splitClarifySubmitConfig(
            followUpConfig,
            msg.generationMetrics?.requestId || '',
          );
          lastConfigRef.current = reusable;
          // 正文照旧发送——后端的 resolveRequirementSourceText 拿它推导需求。
          // clarifyEcho 只影响两件事：这条消息不渲染，答案盖回提问的那张卡片。
          onSend(payload.text, request, {
            clarifyEcho: { answeredMessageId: msg.id, answers: payload.answers },
          });
        } else {
          console.warn('[Clarify] lastConfigRef is null, cannot submit clarify response');
        }
      }}
    />
    </React.Suspense>
  ), [activeBranches, activeSession, allMessages, handleFollowUpSend, handleResubmitUserMessage, handleRetryLastMessage, messages, onOpenRequirementWorkbook, onPlanReviewSubmitted, onResumePlan, onSelectBranch, onSend, onTestHintsResolved, resolvedProjectId, sessionGenerationConfig]);

  return (
    <div className="flex flex-col h-full dark:bg-[#0d1117] relative">
      {messages.length === 0 && historyState === 'loading' ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-sm text-zinc-500" role="status" aria-live="polite">
          <Loader2 className="h-5 w-5 animate-spin text-cyan-500" aria-hidden="true" />
          <span>{t('Loading conversation...')}</span>
        </div>
      ) : messages.length === 0 && historyState === 'error' ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
          <p className="text-sm text-zinc-600 dark:text-zinc-300">{t('Could not load this conversation.')}</p>
          {onRetryHistory && (
            <button type="button" onClick={onRetryHistory} className="min-h-11 rounded-lg bg-cyan-600 px-4 text-sm font-medium text-white hover:bg-cyan-500">
              {t('Retry')}
            </button>
          )}
        </div>
      ) : messages.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-y-auto custom-scrollbar pb-12">
          <div className="mt-auto flex flex-col items-center mb-8">
            <div className="flex items-center gap-3 mb-6">
              <img src={appLogoUrl} alt="RealPLC logo" className="w-12 h-12 object-contain shadow-xl shadow-cyan-500/30 rounded-2xl" />
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-100 dark:to-blue-100 bg-clip-text text-transparent tracking-tight">
                {t('AppTitle')}
              </span>
            </div>
          </div>

          <div className="w-full">
            <ChatInput onSend={handleSend} isLoading={isLoading} onStop={onStop} onNewConversation={onNewConversation} prefillRequest={expertDraft} sessionConfig={activeSession?.sessionConfig} isSuperAdmin={isSuperAdmin} hasActiveClarify={hasActiveClarify} activeClarifyState={activeClarifyState} sessionId={activeSession?.id} projectId={resolvedProjectId} conversationMessages={messages} />
          </div>

          <div className="w-full max-w-4xl mx-auto px-4 mt-2 mb-auto">
            {/* The suggestions are an aid, not a section header — the input box
                above them should stay the heaviest thing on an empty screen. */}
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-xs font-normal text-gray-400 dark:text-zinc-500">{t('Expert Collection')}</h3>
              <button
                onClick={() => setShowAllPrompts(!showAllPrompts)}
                className="flex items-center gap-1 text-xs text-gray-400 transition-colors hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300"
              >
                {showAllPrompts ? t('Less') : t('More')} <span className={`text-xs transition-transform ${showAllPrompts ? '-rotate-90' : 'rotate-90'}`}>›</span>
              </button>
            </div>
            <p className="mb-3 text-xs text-zinc-400 dark:text-zinc-500">{t('Choose a template to preview and edit it before sending.')}</p>
            {showAllPrompts ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleExpertCards.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => selectExpertCard(card)}
                    // The body repeats the prompt and adds a badge; the name is the title.
                    aria-label={card.translate ? t(card.title) : card.title}
                    className="text-left rounded-xl overflow-hidden border border-gray-200 dark:border-cyan-900/30 bg-white dark:bg-[#161b22] hover:border-gray-400 dark:hover:border-cyan-500/50 hover:shadow-md dark:hover:shadow-cyan-900/20 transition-all group flex flex-col h-32 relative"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${card.bgGradient}`}></div>

                    <div className="p-4 relative z-10 flex flex-col h-full justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-gray-700 dark:text-cyan-100 font-medium text-sm group-hover:text-gray-900 dark:group-hover:text-white transition-colors mb-2">
                          <card.IconComponent size={16} className={card.iconColor} />
                          {card.translate ? t(card.title) : card.title}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-cyan-600 leading-relaxed line-clamp-2 group-hover:text-gray-600 dark:group-hover:text-cyan-300 transition-colors">
                          {card.translate ? t(card.prompt) : card.prompt}
                        </p>
                      </div>
                      <div className="mt-3 text-[10px] sm:text-xs font-medium tracking-wide px-2 py-0.5 rounded bg-gray-100 dark:bg-zinc-800/70 border border-gray-200 dark:border-zinc-700 text-gray-500 dark:text-zinc-400 self-start group-hover:bg-gray-200 dark:group-hover:bg-cyan-900/30 group-hover:text-gray-700 dark:group-hover:text-cyan-300 transition-colors border-transparent">
                        {card.translate ? t(card.badgeLabel) : card.badgeLabel}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {visibleExpertCards.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => selectExpertCard(card)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] px-3 py-1.5 text-xs text-gray-600 transition-colors hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 dark:border-white/10 dark:text-zinc-400 dark:hover:border-cyan-600/50 dark:hover:bg-cyan-950/30 dark:hover:text-cyan-200"
                  >
                    <card.IconComponent size={13} className="shrink-0 opacity-70" />
                    <span className="max-w-[220px] truncate">{card.translate ? t(card.title) : card.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="relative min-h-0 flex-1">
            {/* Messages Area */}
            <Virtuoso
              ref={virtuosoRef}
              className="h-full custom-scrollbar"
              data={visibleMessages}
              // Virtuoso owns stick-to-bottom: it keeps following while the user
              // is at the end and stops the moment they scroll up to read.
              followOutput={(isAtBottom) => (isAtBottom ? 'smooth' : false)}
              atBottomThreshold={96}
              atBottomStateChange={(atBottom) => setShowJumpToLatest(!atBottom)}
              increaseViewportBy={{ top: 800, bottom: 800 }}
              components={VIRTUOSO_SPACERS}
              itemContent={(_row, entry) => renderMessage(entry.msg, entry.index)}
            />

            {showJumpToLatest && (
              <button
                type="button"
                onClick={jumpToLatest}
                aria-label={t('Latest')}
                title={t('Latest')}
                className="absolute bottom-4 left-1/2 z-20 flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-700 shadow-lg shadow-black/10 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:text-gray-950 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 dark:border-cyan-500/20 dark:bg-[#0d1117]/95 dark:text-cyan-100 dark:shadow-black/30 dark:hover:border-cyan-400/40 dark:hover:text-white"
              >
                <ChevronDown size={18} strokeWidth={2.4} aria-hidden="true" />
                {isLoading && (
                  <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-cyan-400 ring-2 ring-white dark:ring-[#0d1117]" />
                )}
              </button>
            )}
          </div>

          {/* Input Area */}
          <div className="flex-shrink-0 pt-6 pb-4 relative">
            <ConnectionBanner isGenerating={isLoading} />

            {/* A stopped run is resumed as a fresh turn carrying the partial
                output forward — the backend has no checkpoint to restart from. */}
            {stoppedMessage && !isLoading ? (
              <div className="mx-auto mb-3 flex max-w-[46rem] items-center gap-3 px-4">
                <button
                  type="button"
                  onClick={handleContinueGeneration}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] px-3 py-1.5 text-xs text-zinc-600 transition-colors hover:border-cyan-300 hover:text-cyan-700 dark:border-white/10 dark:text-zinc-300 dark:hover:border-cyan-600/50 dark:hover:text-cyan-200"
                >
                  <RotateCcw size={13} />
                  {t('Continue generating')}
                </button>
                <span className="text-[11px] text-zinc-400 dark:text-zinc-500">
                  {t('Continues as a new turn using what was already written.')}
                </span>
              </div>
            ) : null}
        <ChatInput onSend={handleSend} isLoading={isLoading} onStop={onStop} onNewConversation={onNewConversation} sessionConfig={activeSession?.sessionConfig} isSuperAdmin={isSuperAdmin} hasActiveClarify={hasActiveClarify} activeClarifyState={activeClarifyState} sessionId={activeSession?.id} projectId={resolvedProjectId} conversationMessages={messages} />
          </div>
        </>
      )}
    </div>
  );
};

export const ChatArea = memo(ChatAreaComponent);
